import React, { useState, useEffect } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Globe,
  Heart,
  Users,
  Shield,
  HandHeart,
  Target,
  Zap,
  DollarSign,
  CreditCard,
  Banknote,
  PiggyBank,
  Coins,
  Wallet,
} from "lucide-react";

const Contact = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Numbers",
      details: ["+256 701 816 912", "+256 778 173 081"],
      description: "Call us during business hours for immediate assistance",
      color: "primary",
    },
    {
      icon: Mail,
      title: "Email Address",
      details: ["rakaicommunityhealthinitiative@gmail.com"],
      description: "Send us an email and we'll respond within 24 hours",
      color: "success",
    },
    {
      icon: MapPin,
      title: "Physical Address",
      details: ["Buyamba Trading Centre", "Rakai District, Uganda"],
      description: "Visit our office for in-person consultations",
      color: "warning",
    },
    {
      icon: Globe,
      title: "Postal Address",
      details: ["P.O. Box 277", "Kyotera – Uganda"],
      description: "Send us mail through our postal address",
      color: "info",
    },
  ];

  const officeHours = [
    { day: "Monday - Friday", hours: "8:00 AM - 5:00 PM" },
    { day: "Saturday", hours: "9:00 AM - 1:00 PM" },
    { day: "Sunday", hours: "Closed" },
    { day: "Public Holidays", hours: "Closed" },
  ];

  return (
    <div className="contact-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Floating Contact Elements */}
      <div className="contact-background">
        <div className="floating-element element-1"></div>
        <div className="floating-element element-2"></div>
        <div className="floating-element element-3"></div>
        <div className="floating-element element-4"></div>
        <div className="contact-shape shape-1"></div>
        <div className="contact-shape shape-2"></div>
        <div className="contact-shape shape-3"></div>
      </div>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8">
              <div className="hero-content">
                <span className="hero-badge">Contact Us</span>
                <h1 className="hero-title">Let's Work Together for Change</h1>
                <p className="hero-subtitle">
                  Ready to make a difference in the fight against Sexual and
                  Gender-Based Violence? Connect with our team to explore
                  partnership opportunities, volunteer programs, or to learn
                  more about our impact in Rakai District.
                </p>
                <div className="hero-stats">
                  <div className="stat-item">
                    <Clock className="stat-icon" size={24} />
                    <span className="stat-text">24hr Response Time</span>
                  </div>
                  <div className="stat-item">
                    <Users className="stat-icon" size={24} />
                    <span className="stat-text">Multilingual Support</span>
                  </div>
                  <div className="stat-item">
                    <Shield className="stat-icon" size={24} />
                    <span className="stat-text">Confidential Services</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="hero-visual">
                <div className="hero-shape-container">
                  <div className="hero-shape shape-primary"></div>
                  <div className="hero-shape shape-secondary"></div>
                  <div className="hero-shape shape-accent"></div>
                  <div className="hero-dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                  <div className="hero-rings">
                    <div className="ring ring-1"></div>
                    <div className="ring ring-2"></div>
                    <div className="ring ring-3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Get in Touch</span>
            <h2 className="section-title">Contact Information</h2>
            <p className="section-description">
              Multiple ways to reach us. Choose what works best for you.
            </p>
          </div>

          <div className="row g-4">
            {contactInfo.map((info, index) => (
              <div key={index} className="col-lg-6 col-md-6">
                <div
                  className="contact-info-card"
                  style={{
                    transform: `translateY(${
                      Math.sin((scrollY + index * 100) * 0.005) * 5
                    }px)`,
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className={`info-icon bg-${info.color}`}>
                    <info.icon size={28} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">{info.title}</h4>
                    <div className="info-details">
                      {info.details.map((detail, idx) => (
                        <div key={idx} className="detail-item">
                          {detail}
                        </div>
                      ))}
                    </div>
                    <p className="info-description">{info.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Hours Section */}
      <section className="office-hours-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12">
              <div className="office-hours-card">
                <div className="hours-header">
                  <Clock size={32} className="hours-icon" />
                  <div>
                    <h3 className="hours-title">Office Hours</h3>
                    <p className="hours-subtitle">
                      We're here to help during these times
                    </p>
                  </div>
                </div>
                <div className="hours-grid">
                  {officeHours.map((schedule, index) => (
                    <div key={index} className="hours-item">
                      <span className="hours-day">{schedule.day}</span>
                      <span className="hours-time">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="emergency-notice">
                  <Shield className="emergency-icon" size={20} />
                  <span>For emergencies, call +256 701 816 912 anytime</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Support Section */}
<section className="support-section">
  <div className="container">
    <div className="section-header text-center">
      <span className="section-badge">Support Us</span>
      <h2 className="section-title">Help Us Make a Difference</h2>
      <p className="section-description">
        We're seeking <strong>$60,000</strong> to implement a project addressing Sexual & Gender-Based Violence in Rakai District, Uganda. Your support—financial or in-kind—can change lives and create lasting impact in our communities.
      </p>
    </div>

    {/* Donation Contact Card */}
    <div className="row justify-content-center">
      <div className="col-lg-12">
        <div className="donation-contact-card">
          <div className="donation-header">
            <DollarSign className="donation-icon" size={32} />
            <h3 className="donation-title">For Any Donations Please Contact:</h3>
          </div>
          <div className="donation-content">
            <div className="founder-info">
              <h4 className="founder-title">Founder & CEO</h4>
              <h5 className="founder-name">Nicholas Ssewankambwe</h5>
            </div>
            <div className="contact-details">
              <div className="contact-item">
                <Mail className="contact-item-icon" size={20} />
                <div className="contact-item-content">
                  <span className="contact-label">Email:</span>
                  <div className="email-list">
                    <a href="mailto:nssewankambwe2023@gmail.com" className="email-link">
                      nssewankambwe2023@gmail.com
                    </a>
                    <a href="mailto:rakaicommunityhealthinitiative@gmail.com" className="email-link">
                      rakaicommunityhealthinitiative@gmail.com
                    </a>
                  </div>
                </div>
              </div>
              <div className="contact-item">
                <Phone className="contact-item-icon" size={20} />
                <div className="contact-item-content">
                  <span className="contact-label">Phone:</span>
                  <a href="tel:+256701816912" className="phone-link">+256 701 816 912</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Impact Highlights */}
    <div className="row mt-5">
      <div className="col-lg-10 mx-auto">
        <div className="support-context-card">
          <div className="context-header">
            <Zap className="context-icon" size={28} />
            <h3 className="context-title">Why Your Support Matters</h3>
          </div>
          <div className="context-content">
            <p>
              <strong>RACOHI</strong> operates as a grassroots organization with minimal overhead, 
              ensuring that your contributions directly fund our programs and services. We believe 
              in transparency and accountability, providing regular updates on how your support 
              is making a tangible difference.
            </p>
            <div className="context-highlights">
              <div className="highlight-item">
                <div className="highlight-number">85%</div>
                <div className="highlight-text">of donations go directly to programs</div>
              </div>
              <div className="highlight-item">
                <div className="highlight-number">100%</div>
                <div className="highlight-text">transparency in financial reporting</div>
              </div>
              <div className="highlight-item">
                <div className="highlight-number">24/7</div>
                <div className="highlight-text">emergency support services</div>
              </div>
            </div>
            <p>
              Whether you choose to donate, volunteer, or help spread awareness, you become part of a movement 
              that's creating safer communities and empowering survivors of gender-based violence. Together, 
              we can build a future where everyone lives free from violence and discrimination.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<style jsx>{`
  /* Support Section */
  .support-section {
    padding: 2rem 0;
    background: white;
  }

  .impact-stat-card {
    background: var(--light-gray);
    border-radius: 1.5rem;
    padding: 2.5rem 2rem;
    text-align: center;
    transition: all 0.3s ease;
    height: 100%;
  }

  .impact-stat-card:hover {
    transform: translateY(-5px);
    background: rgba(37, 99, 235, 0.05);
  }

  .impact-stat-card .stat-icon {
    color: var(--primary-blue);
    margin-bottom: 1rem;
  }

  .stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--secondary-blue);
    margin-bottom: 0.5rem;
    line-height: 1;
  }

  .stat-label {
    color: var(--text-gray);
    font-weight: 500;
    margin: 0;
    font-size: 1rem;
  }

  /* Donation Contact Card */
  .donation-contact-card {
    background: white;
    border-radius: 1.5rem;
    padding: 2.5rem;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
    border: 2px solid var(--accent-orange);
    position: relative;
    overflow: hidden;
  }

  .donation-contact-card::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100px;
    height: 100px;
    background: linear-gradient(45deg, var(--accent-orange), var(--accent-teal));
    border-radius: 50%;
    opacity: 0.1;
    animation: donationFloat 6s ease-in-out infinite;
  }

  @keyframes donationFloat {
    0%, 100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-15px) rotate(180deg);
    }
  }

  .donation-header {
    text-align: center;
    margin-bottom: 2rem;
    position: relative;
    z-index: 2;
  }

  .donation-icon {
    color: var(--accent-orange);
    margin-bottom: 1rem;
  }

  .donation-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--secondary-blue);
    margin: 0;
    line-height: 1.4;
  }

  .donation-content {
    position: relative;
    z-index: 2;
  }

  .founder-info {
    text-align: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid var(--light-gray);
  }

  .founder-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-gray);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .founder-name {
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--secondary-blue);
    margin: 0;
  }

  .contact-details {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  .contact-item-icon {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  .contact-item-content {
    flex: 1;
  }

  .contact-label {
    display: block;
    font-weight: 600;
    color: var(--secondary-blue);
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  .email-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .email-link {
    color: var(--primary-blue);
    text-decoration: none;
    font-weight: 500;
    padding: 0.75rem 1rem;
    background: rgba(37, 99, 235, 0.05);
    border-radius: 0.5rem;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    display: block;
    word-break: break-all;
  }

  .email-link:hover {
    background: rgba(37, 99, 235, 0.1);
    color: var(--secondary-blue);
    transform: translateX(5px);
  }

  .phone-link {
    color: var(--success-green);
    text-decoration: none;
    font-weight: 600;
    font-size: 1.1rem;
    padding: 0.75rem 1.5rem;
    background: rgba(16, 185, 129, 0.1);
    border-radius: 50px;
    transition: all 0.3s ease;
    display: inline-block;
  }

  .phone-link:hover {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
    transform: translateY(-2px);
  }

  /* Support Context Card */
  .support-context-card {
    background: linear-gradient(135deg, var(--primary-blue), var(--accent-teal));
    border-radius: 2rem;
    padding: 3rem;
    color: white;
    position: relative;
    overflow: hidden;
  }

  .support-context-card::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    animation: contextFloat 8s ease-in-out infinite;
  }

  .support-context-card::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -30%;
    width: 150px;
    height: 150px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
    animation: contextFloat 6s ease-in-out infinite reverse;
  }

  @keyframes contextFloat {
    0%, 100% {
      transform: translateY(0px) translateX(0px);
    }
    50% {
      transform: translateY(-20px) translateX(20px);
    }
  }

  .context-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
    position: relative;
    z-index: 2;
  }

  .context-icon {
    color: white;
    flex-shrink: 0;
  }

  .context-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
  }

  .context-content {
    position: relative;
    z-index: 2;
  }

  .context-content p {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 2rem;
    opacity: 0.95;
  }

  .context-highlights {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 2rem;
    margin: 2.5rem 0;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    backdrop-filter: blur(10px);
  }

  .highlight-item {
    text-align: center;
  }

  .highlight-number {
    font-size: 2.2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: white;
  }

  .highlight-text {
    font-size: 0.9rem;
    opacity: 0.9;
    line-height: 1.4;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .donation-contact-card {
      padding: 2rem 1.5rem;
    }

    .founder-name {
      font-size: 1.5rem;
    }

    .contact-item {
      flex-direction: column;
      text-align: center;
      gap: 0.75rem;
    }

    .support-context-card {
      padding: 2rem 1.5rem;
    }

    .context-highlights {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }
`}</style>


      {/* Location Section */}
      <section className="location-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-badge">Visit Us</span>
            <h2 className="section-title">Our Location</h2>
            <p className="section-description">
              Find us in the heart of Rakai District, easily accessible for
              community members and partners.
            </p>
          </div>

          {/* Main Content Row */}
          <div className="row align-items-start">
            {/* Location Card */}
            <div className="col-md-6 mb-4">
              <div className="location-card d-flex align-items-start">
                <MapPin className="location-icon me-3" size={40} />
                <div className="location-details">
                  <h4 className="location-title">RACOHI Office</h4>
                  <div className="location-address">
                    <p>Buyamba Trading Centre</p>
                    <p>Rakai District, Central Region</p>
                    <p>Uganda, East Africa</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Accessibility Info */}
            <div className="col-md-6 mb-4">
              <div className="accessibility-info">
                <h5 className="accessibility-title">Accessibility Features</h5>
                <ul className="accessibility-list">
                  <li>Wheelchair accessible entrance</li>
                  <li>Public transportation nearby</li>
                  <li>Free parking available</li>
                  <li>Multilingual staff support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --success-green: #10b981;
          --text-gray: #6b7280;
          --light-gray: #f8fafc;
          --danger-red: #ef4444;
          --info-blue: #3b82f6;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #374151;
          padding-top: 80px;
          overflow-x: hidden;
        }

        /* Floating Background Elements */
        .contact-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: -1;
        }

        .floating-element {
          position: absolute;
          background: linear-gradient(
            45deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          opacity: 0.03;
          border-radius: 50%;
          animation: contactFloat 20s ease-in-out infinite;
        }

        .floating-element.element-1 {
          width: 150px;
          height: 150px;
          top: 10%;
          right: 15%;
          animation-delay: 0s;
        }

        .floating-element.element-2 {
          width: 100px;
          height: 100px;
          bottom: 20%;
          left: 10%;
          animation-delay: 5s;
        }

        .floating-element.element-3 {
          width: 80px;
          height: 80px;
          top: 50%;
          right: 25%;
          animation-delay: 10s;
        }

        .floating-element.element-4 {
          width: 120px;
          height: 120px;
          bottom: 40%;
          right: 10%;
          animation-delay: 15s;
        }

        .contact-shape {
          position: absolute;
          border: 2px solid var(--accent-orange);
          opacity: 0.08;
          animation: shapeRotate 15s linear infinite;
        }

        .contact-shape.shape-1 {
          width: 60px;
          height: 60px;
          top: 25%;
          left: 20%;
          border-radius: 20px;
          animation-delay: 0s;
        }

        .contact-shape.shape-2 {
          width: 40px;
          height: 40px;
          bottom: 30%;
          right: 30%;
          border-radius: 50%;
          animation-delay: 5s;
        }

        .contact-shape.shape-3 {
          width: 80px;
          height: 80px;
          top: 70%;
          left: 60%;
          border-radius: 15px;
          animation-delay: 10s;
        }

        @keyframes contactFloat {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          25% {
            transform: translateY(-30px) translateX(20px);
          }
          50% {
            transform: translateY(-60px) translateX(0px);
          }
          75% {
            transform: translateY(-30px) translateX(-20px);
          }
        }

        @keyframes shapeRotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hero Section */
        .contact-hero {
          padding: 4rem 0;
          background: linear-gradient(
            135deg,
            var(--light-gray) 0%,
            #ffffff 100%
          );
          position: relative;
        }

        .min-vh-60 {
          min-height: 60vh;
        }

        .hero-content {
          max-width: 700px;
        }

        .hero-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          display: inline-block;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .hero-stats {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: white;
          padding: 1rem 1.5rem;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .stat-icon {
          color: var(--primary-blue);
        }

        .stat-text {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-gray);
        }

        /* Hero Visual */
        .hero-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
        }

        .hero-shape-container {
          position: relative;
          width: 300px;
          height: 300px;
        }

        .hero-shape {
          position: absolute;
          border-radius: 50%;
          animation: heroFloat 6s ease-in-out infinite;
        }

        .hero-shape.shape-primary {
          width: 180px;
          height: 180px;
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          top: 20px;
          left: 20px;
          opacity: 0.9;
          animation-delay: 0s;
        }

        .hero-shape.shape-secondary {
          width: 120px;
          height: 120px;
          background: linear-gradient(45deg, var(--accent-orange), #fbbf24);
          top: 100px;
          right: 30px;
          opacity: 0.8;
          animation-delay: 2s;
        }

        .hero-shape.shape-accent {
          width: 80px;
          height: 80px;
          background: linear-gradient(225deg, var(--success-green), #34d399);
          bottom: 50px;
          left: 50px;
          opacity: 0.7;
          animation-delay: 4s;
        }

        .hero-dots {
          position: absolute;
          top: 10px;
          right: 10px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          transform: rotate(15deg);
          animation: dotRotate 10s linear infinite;
        }

        .dot {
          width: 8px;
          height: 8px;
          background: var(--primary-blue);
          border-radius: 50%;
          opacity: 0.4;
          animation: dotPulse 2s ease-in-out infinite;
        }

        .dot:nth-child(1) {
          animation-delay: 0s;
        }
        .dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        .dot:nth-child(4) {
          animation-delay: 0.6s;
        }
        .dot:nth-child(5) {
          animation-delay: 0.8s;
        }
        .dot:nth-child(6) {
          animation-delay: 1s;
        }
        .dot:nth-child(7) {
          animation-delay: 1.2s;
        }
        .dot:nth-child(8) {
          animation-delay: 1.4s;
        }
        .dot:nth-child(9) {
          animation-delay: 1.6s;
        }

        .hero-rings {
          position: absolute;
          inset: 0;
        }

        .ring {
          position: absolute;
          border: 2px solid;
          border-radius: 50%;
          animation: ringPulse 4s ease-in-out infinite;
        }

        .ring.ring-1 {
          width: 250px;
          height: 250px;
          border-color: rgba(37, 99, 235, 0.2);
          top: 25px;
          left: 25px;
          animation-delay: 0s;
        }

        .ring.ring-2 {
          width: 200px;
          height: 200px;
          border-color: rgba(6, 182, 212, 0.15);
          top: 50px;
          left: 50px;
          animation-delay: 1.5s;
        }

        .ring.ring-3 {
          width: 320px;
          height: 320px;
          border-color: rgba(245, 158, 11, 0.1);
          top: -10px;
          left: -10px;
          animation-delay: 3s;
        }

        @keyframes heroFloat {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes dotRotate {
          from {
            transform: rotate(15deg);
          }
          to {
            transform: rotate(375deg);
          }
        }

        @keyframes dotPulse {
          0%,
          100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.2);
          }
        }

        @keyframes ringPulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.1;
          }
        }

        /* Section Headers */
        .section-header {
          margin-bottom: 4rem;
        }

        .section-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .section-title {
          font-size: clamp(2.5rem, 4vw, 3.5rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Contact Info Section */
        .contact-info-section {
          padding: 2rem 0;
          background: white;
        }

        .contact-info-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          height: 100%;
          transition: all 0.3s ease;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .contact-info-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
        }

        .info-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto 1.5rem;
        }

        .info-icon.bg-primary {
          background-color: var(--primary-blue);
        }
        .info-icon.bg-success {
          background-color: var(--success-green);
        }
        .info-icon.bg-warning {
          background-color: var(--accent-orange);
        }
        .info-icon.bg-info {
          background-color: var(--info-blue);
        }

        .info-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .info-details {
          margin-bottom: 1rem;
        }

        .detail-item {
          color: var(--secondary-blue);
          font-weight: 600;
          font-size: 0.95rem;
          line-height: 1.4;
          margin-bottom: 0.25rem;
        }

        .info-description {
          color: var(--text-gray);
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        /* Office Hours Section */
        .office-hours-section {
          padding: 3rem 0;
          background: var(--light-gray);
        }

        .office-hours-card {
          background: white;
          border-radius: 2rem;
          padding: 3rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .hours-header {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
          text-align: left;
        }

        .hours-icon {
          color: var(--primary-blue);
          flex-shrink: 0;
        }

        .hours-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin: 0;
        }

        .hours-subtitle {
          color: var(--text-gray);
          margin: 0;
          font-size: 1.1rem;
        }

        .hours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .hours-item {
          background: var(--light-gray);
          border-radius: 1rem;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .hours-item:hover {
          background: rgba(37, 99, 235, 0.05);
          transform: translateY(-2px);
        }

        .hours-day {
          display: block;
          font-weight: 700;
          color: var(--secondary-blue);
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .hours-time {
          color: var(--text-gray);
          font-size: 0.9rem;
        }

        .emergency-notice {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          background: rgba(239, 68, 68, 0.05);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 1rem;
          padding: 1rem;
          color: var(--danger-red);
          font-weight: 600;
        }

        .emergency-icon {
          flex-shrink: 0;
        }

        .impact-stat-card {
          background: var(--light-gray);
          border-radius: 1.5rem;
          padding: 2.5rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          height: 100%;
        }

        .impact-stat-card:hover {
          transform: translateY(-5px);
          background: rgba(37, 99, 235, 0.05);
        }

        .impact-stat-card .stat-icon {
          color: var(--primary-blue);
          margin-bottom: 1rem;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 0.5rem;
          line-height: 1;
        }

        .stat-label {
          color: var(--text-gray);
          font-weight: 500;
          margin: 0;
          font-size: 1rem;
        }

        /* Support Method Cards */
        .support-method-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          height: 100%;
          transition: all 0.3s ease;
          display: flex;
          gap: 1.5rem;
        }

        .support-method-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
        }

        .method-icon-container {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .method-icon-container.bg-primary {
          background-color: var(--primary-blue);
        }
        .method-icon-container.bg-danger {
          background-color: var(--danger-red);
        }
        .method-icon-container.bg-success {
          background-color: var(--success-green);
        }
        .method-icon-container.bg-warning {
          background-color: var(--accent-orange);
        }

        .method-content {
          flex: 1;
        }

        .method-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .method-description {
          color: var(--text-gray);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .method-options {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .method-option {
          background: var(--light-gray);
          color: var(--text-gray);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        /* Support Context Card */
        .support-context-card {
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          border-radius: 2rem;
          padding: 3rem;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .support-context-card::before {
          content: "";
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200px;
          height: 200px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          animation: contextFloat 8s ease-in-out infinite;
        }

        .support-context-card::after {
          content: "";
          position: absolute;
          bottom: -30%;
          left: -30%;
          width: 150px;
          height: 150px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          animation: contextFloat 6s ease-in-out infinite reverse;
        }

        @keyframes contextFloat {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(20px);
          }
        }

        .context-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          position: relative;
          z-index: 2;
        }

        .context-icon {
          color: white;
          flex-shrink: 0;
        }

        .context-title {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
        }

        .context-content {
          position: relative;
          z-index: 2;
        }

        .context-content p {
          font-size: 1.1rem;
          line-height: 1.7;
          margin-bottom: 2rem;
          opacity: 0.95;
        }

        .context-highlights {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 2rem;
          margin: 2.5rem 0;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          backdrop-filter: blur(10px);
        }

        .highlight-item {
          text-align: center;
        }

        .highlight-number {
          font-size: 2.2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: white;
        }

        .highlight-text {
          font-size: 0.9rem;
          opacity: 0.9;
          line-height: 1.4;
        }

        /* Location Section */
        .location-section {
          padding: 6rem 0;
          background: var(--light-gray);
        }

        .location-info {
          padding-right: 2rem;
        }

        .location-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .location-icon {
          color: var(--primary-blue);
          flex-shrink: 0;
          margin-top: 0.5rem;
        }

        .location-details {
          flex: 1;
        }

        .location-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .location-address {
          margin-bottom: 1.5rem;
        }

        .location-address p {
          color: var(--text-gray);
          margin-bottom: 0.25rem;
          line-height: 1.4;
          font-size: 1rem;
        }

        .accessibility-info {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .accessibility-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .accessibility-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .accessibility-list li {
          padding: 0.5rem 0;
          color: var(--text-gray);
          position: relative;
          padding-left: 1.5rem;
        }

        .accessibility-list li::before {
          content: "✓";
          color: var(--success-green);
          font-weight: bold;
          position: absolute;
          left: 0;
        }

        /* Map Placeholder */
        .map-placeholder {
          background: white;
          border-radius: 1.5rem;
          padding: 3rem;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .map-content {
          max-width: 300px;
        }

        .map-icon {
          color: var(--primary-blue);
          margin-bottom: 1.5rem;
        }

        .map-content h4 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .map-content p {
          color: var(--text-gray);
          margin-bottom: 1.5rem;
          line-height: 1.5;
        }

        .map-coordinates {
          background: var(--light-gray);
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .coordinate-label {
          display: block;
          font-size: 0.8rem;
          color: var(--text-gray);
          margin-bottom: 0.25rem;
        }

        .coordinate-value {
          font-family: "Courier New", monospace;
          font-weight: 600;
          color: var(--primary-blue);
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .hero-visual {
            margin-top: 3rem;
            min-height: 300px;
          }

          .hero-shape-container {
            width: 250px;
            height: 250px;
          }

          .hero-shape.shape-primary {
            width: 150px;
            height: 150px;
          }

          .hero-shape.shape-secondary {
            width: 100px;
            height: 100px;
          }

          .hero-shape.shape-accent {
            width: 70px;
            height: 70px;
          }

          .support-method-card {
            flex-direction: column;
            text-align: center;
          }

          .method-icon-container {
            margin: 0 auto 1.5rem;
          }

          .context-highlights {
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 1.5rem;
          }
        }

        @media (max-width: 768px) {
          body {
            padding-top: 70px;
          }

          .contact-background {
            display: none;
          }

          .hero-stats {
            flex-direction: column;
            gap: 1rem;
          }

          .stat-item {
            justify-content: center;
          }

          .contact-info-card {
            padding: 2rem 1.5rem;
          }

          .office-hours-card {
            padding: 2rem 1.5rem;
          }

          .hours-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .hours-grid {
            grid-template-columns: 1fr;
          }

          .support-context-card {
            padding: 2rem 1.5rem;
          }

          .context-highlights {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .location-info {
            padding-right: 0;
            margin-bottom: 3rem;
          }

          .location-card {
            padding: 2rem 1.5rem;
            flex-direction: column;
            text-align: center;
          }

          .map-placeholder {
            padding: 2rem 1.5rem;
          }

          .hero-visual {
            min-height: 250px;
          }

          .hero-shape-container {
            width: 200px;
            height: 200px;
          }

          .hero-shape.shape-primary {
            width: 120px;
            height: 120px;
          }

          .hero-shape.shape-secondary {
            width: 80px;
            height: 80px;
          }

          .hero-shape.shape-accent {
            width: 60px;
            height: 60px;
          }
        }

        @media (max-width: 576px) {
          .contact-info-card {
            padding: 1.5rem;
          }

          .info-icon {
            width: 60px;
            height: 60px;
          }

          .support-method-card {
            padding: 2rem 1.5rem;
          }

          .method-icon-container {
            width: 60px;
            height: 60px;
          }

          .emergency-notice {
            flex-direction: column;
            text-align: center;
            gap: 0.5rem;
          }
        }

        /* Print Styles */
        @media print {
          .contact-background {
            display: none;
          }

          .support-context-card {
            background: var(--light-gray) !important;
            color: var(--text-gray) !important;
          }

          .emergency-notice {
            border: 2px solid var(--danger-red);
          }
        }

        /* Accessibility */
        .contact-info-card:focus,
        .support-method-card:focus,
        .impact-stat-card:focus {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          .hero-shape,
          .hero-dots,
          .ring,
          .dot,
          .floating-element,
          .contact-shape {
            animation: none;
          }

          .support-context-card::before,
          .support-context-card::after {
            animation: none;
          }
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Loading States */
        .contact-info-card {
          animation-fill-mode: both;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: var(--light-gray);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--primary-blue);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--secondary-blue);
        }
      `}</style>

      {/* Bootstrap JS */}
      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </div>
  );
};

export default Contact;
